/**
 * Client dell'API Youppybuy.
 *
 * Due basi diverse di proposito: il browser passa da NEXT_PUBLIC_API_URL (ed e'
 * soggetto a CORS), il server di Next chiama l'API direttamente e non lo e'.
 * Le pagine prodotto sono renderizzate lato server perche' devono essere
 * indicizzabili: un catalogo che non compare nei motori di ricerca non serve.
 *
 * Nota sul modello: **il fornitore non compare**. Nessun tipo qui dentro porta
 * ragione sociale, indirizzo o contatti di chi produce, perche' l'API non li
 * restituisce piu'. Resta il paese di produzione, che non identifica nessuno.
 */

const BROWSER_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://127.0.0.1:8000";
const SERVER_BASE = process.env.API_URL ?? BROWSER_BASE;

const base = () => (typeof window === "undefined" ? SERVER_BASE : BROWSER_BASE);

/** Testo multilingua: {"it": "...", "en": "..."} */
export type Localized = Record<string, string>;

/** Il prezzo dipende da chi guarda: impresa abbonata, impresa, privato. */
export type Price = {
  net_price: string;
  discount_percent: string;
  vat_rate: string;
  gross_price: string;
  show_gross: boolean;
  currency: string;
};

/**
 * Il voto di un prodotto.
 *
 * `demo_count` non è un dettaglio di servizio: dice quante delle valutazioni
 * contate sono **dimostrative**, cioè scritte da noi per disegnare le pagine.
 * Viaggia sempre insieme alla media proprio perché mostrare la media senza
 * dirlo sarebbe una pratica commerciale sleale, e un dato che arriva in un
 * altro endpoint è un dato che prima o poi qualcuno non chiede.
 */
export type Rating = {
  reviews_count: number;
  /** `null` quando non c'è nessuna valutazione. Numerico come stringa: viene da Postgres. */
  rating_avg: string | null;
  demo_count: number;
  stelle_5?: number;
  stelle_4?: number;
  stelle_3?: number;
  stelle_2?: number;
  stelle_1?: number;
};

export type Review = {
  id: string;
  origin: "cliente" | "dimostrativa";
  rating: number;
  title: string | null;
  body: string | null;
  author_display: string;
  country_code: string | null;
  language: string;
  created_at: string;
};

export type ReviewableItem = {
  order_item_id: string;
  product_id: string;
  description: string;
  already_done: boolean;
};

export type ProductHit = {
  product_id: string;
  country_code: string;
  product_name: Localized;
  product_description: Localized;
  materials: string[];
  processes: string[];
  certifications: string[];
  target_sectors: string[];
  min_order_quantity: number | null;
  lead_time_days: number | null;
  unit: string | null;
  image_uri: string | null;
  score: number;
  vector_similarity: number | null;
  text_rank: number | null;
  /** Attaccato dall'API: dipende da chi guarda, quindi non sta nell'indice. */
  price?: Price | null;
  /** Attaccato dall'API come il prezzo: è un aggregato, non un campo del prodotto. */
  rating?: Rating | null;
};

export type ImageHit = Omit<ProductHit, "score" | "text_rank"> & {
  vector_similarity: number;
};

/** La scheda prodotto: la vista `public_products` piu' il prezzo calcolato. */
export type Product = {
  product_id: string;
  country_code: string;
  name: Localized;
  description: Localized;
  materials: string[];
  processes: string[];
  certifications: string[];
  target_sectors: string[];
  min_order_quantity: number | null;
  lead_time_days: number | null;
  unit: string | null;
  is_purchasable: boolean;
  image_uri: string | null;
  updated_at: string;
  price: Price | null;
  rating: Rating | null;
};

export type SearchIntent = {
  semantic_query: string;
  countries: string[];
  nace_prefixes: string[];
  materials: string[];
  processes: string[];
  sectors: string[];
  certifications: string[];
  max_order_quantity: number | null;
  max_lead_time_days: number | null;
  language: string | null;
};

export type TextSearchResponse = {
  intent: SearchIntent;
  interpreted_by: "llm" | "heuristic" | "explicit";
  results: ProductHit[];
  relaxed_filters: string[];
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/* Oltre questo tempo l'API si considera non raggiungibile. Un backend che non
   risponde — il database spento, un blocco — lasciava girare il filo all'infinito:
   sulla chat sono i tre puntini che non finiscono mai, cioe' per chi guarda
   un'app rotta. Meglio un errore chiaro dopo un'attesa generosa (l'interpretazione
   della frase e la ricerca vettoriale possono volerci qualche secondo) che uno
   spinner eterno. */
const TIMEOUT_MS = 25000;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const taglia = AbortSignal.timeout(TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${base()}${path}`, {
      ...init,
      headers: { Accept: "application/json", ...init?.headers },
      cache: "no-store",
      signal: taglia,
    });
  } catch (e) {
    // Timeout o rete caduta: un unico messaggio comprensibile, mai un errore
    // tecnico in inglese sotto gli occhi di chi compra.
    if (e instanceof DOMException && e.name === "TimeoutError") {
      throw new ApiError("Il catalogo non risponde in questo momento. Riprova fra poco.", 504);
    }
    throw new ApiError("Il catalogo non risponde in questo momento. Riprova fra poco.", 503);
  }

  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    // Senza un dettaglio dall'API, un 500 arriverebbe all'utente come
    // "Internal Server Error": in inglese e senza dire cosa fare.
    const messaggio =
      detail?.detail ??
      (response.status >= 500
        ? "Il catalogo non risponde in questo momento. Riprova fra poco."
        : response.statusText);
    throw new ApiError(messaggio, response.status);
  }
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Ricerca
// ---------------------------------------------------------------------------
export function searchText(query: string, limit = 20) {
  return request<TextSearchResponse>("/search/text", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, limit }),
  });
}

/** Riesegue la ricerca su filtri modificati a mano, senza reinterpretare la frase. */
export function executeSearch(intent: SearchIntent, limit = 20) {
  return request<TextSearchResponse>("/search/execute", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ intent, limit }),
  });
}

export function searchImage(file: File, countries?: string[]) {
  const form = new FormData();
  form.append("file", file);
  if (countries?.length) form.append("countries", countries.join(","));

  return request<{ results: ImageHit[] }>("/search/image", {
    method: "POST",
    body: form,
  });
}

// ---------------------------------------------------------------------------
// Prodotti
// ---------------------------------------------------------------------------
export function getProduct(productId: string, customerId?: string) {
  const query = customerId ? `?customer_id=${customerId}` : "";
  return request<Product>(`/products/${productId}${query}`);
}

/** Le sfaccettature sono i valori che hanno davvero prodotti dietro: i filtri si
 *  costruiscono da qui, cosi' non se ne offre uno che porta al vuoto. */
export type Facets = {
  paesi: string[] | null;
  materiali: string[] | null;
  lavorazioni: string[] | null;
};

export function listProducts(
  params: {
    country?: string;
    material?: string;
    /** Slug di un reparto di tipo settore. */
    section?: string;
    limit?: number;
    offset?: number;
  } = {},
) {
  const query = new URLSearchParams();
  if (params.country) query.set("country", params.country);
  if (params.material) query.set("material", params.material);
  if (params.section) query.set("section", params.section);
  query.set("limit", String(params.limit ?? 24));
  query.set("offset", String(params.offset ?? 0));

  return request<{
    results: Product[];
    total: number;
    facets: Facets;
    limit: number;
    offset: number;
  }>(`/products?${query}`);
}

/** La scheda di risultato parla il linguaggio della ricerca; il catalogo quello
 *  di `public_products`. Stessi dati, due nomi: si adattano qui una volta sola. */
export function comeRisultato(p: Product): ProductHit {
  return {
    product_id: p.product_id,
    country_code: p.country_code,
    product_name: p.name,
    product_description: p.description,
    materials: p.materials,
    processes: p.processes,
    certifications: p.certifications,
    target_sectors: p.target_sectors,
    min_order_quantity: p.min_order_quantity,
    lead_time_days: p.lead_time_days,
    unit: p.unit,
    image_uri: p.image_uri,
    score: 0,
    vector_similarity: null,
    text_rank: null,
    price: p.price,
    rating: p.rating,
  };
}

/** Rivendicazione della scheda: serve ai fornitori, non ai compratori. */
export function claimListing(companyId: string, email: string, marketingConsent: boolean) {
  return request<{ claim_id: string; status: string; message: string }>(
    `/companies/${companyId}/claim`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, marketing_consent: marketingConsent }),
    },
  );
}

// ---------------------------------------------------------------------------
// Formattazione
// ---------------------------------------------------------------------------
/** Le immagini sono servite dall'API: storage_uri e' un percorso, non un URL. */
export function mediaUrl(uri: string | null): string | null {
  if (!uri) return null;
  return uri.startsWith("/") ? `${BROWSER_BASE}${uri}` : uri;
}

/** Sceglie la lingua migliore disponibile da un campo multilingua. */
export function localized(value: Localized | null | undefined, locale = "it"): string {
  if (!value) return "";
  return value[locale] ?? value.en ?? Object.values(value)[0] ?? "";
}

export function formattaPrezzo(price: Price | null): string | null {
  if (!price) return null;
  const valore = price.show_gross ? price.gross_price : price.net_price;
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: price.currency,
  }).format(Number(valore));
}

const NOMI_PAESE: Record<string, string> = {
  AT: "Austria", BE: "Belgio", CZ: "Repubblica Ceca", DE: "Germania",
  DK: "Danimarca", ES: "Spagna", FI: "Finlandia", FR: "Francia",
  GB: "Regno Unito", IE: "Irlanda", IT: "Italia", NL: "Paesi Bassi",
  PL: "Polonia", PT: "Portogallo", SE: "Svezia",
};

export const nomePaese = (codice: string) => NOMI_PAESE[codice.trim()] ?? codice.trim();

// ---------------------------------------------------------------------------
// Configurazione guidata
//
// Meta' del catalogo e' fatto di lavorazioni su disegno: non sono prodotti a
// scaffale e non diventano ordinabili finche' qualcuno non dice quanti, in che
// materiale, con quale tolleranza. Le domande arrivano dall'API perche'
// dipendono dal prodotto: qui non ce n'e' nessuna scritta a mano.
// ---------------------------------------------------------------------------
export type OptionType = "quantity" | "choice" | "text" | "number" | "date" | "file";

export type ProductOption = {
  code: string;
  label: Localized;
  help: Localized;
  type: OptionType;
  is_required: boolean;
  choices: { value: string; label: Localized }[] | null;
  min_value: string | null;
  max_value: string | null;
  unit: string | null;
  position: number;
};

export type Estimate = {
  unit_net_price: string;
  quantity: number;
  total_net_price: string;
  vat_rate: string;
  total_gross_price: string;
  show_gross: boolean;
  currency: string;
};

export type ConfigurationState = {
  token: string;
  status: "draft" | "ready" | "ordered" | "abandoned";
  product: Product | null;
  options: ProductOption[];
  answers: Record<string, string>;
  quantity: number | null;
  notes: string | null;
  problems: { option_code: string; problem: string }[];
  complete: boolean;
  next_question: ProductOption | null;
  estimate: Estimate | null;
  frozen_price: {
    unit_net_price: string;
    total_net_price: string;
    vat_rate: string;
    total_gross_price: string;
    currency: string;
    priced_at: string;
  } | null;
  /** Presente solo nella risposta a interpretConfiguration. */
  interpreted?: { method: "llm" | "heuristic"; extracted: Record<string, unknown> };
};

const json = { "Content-Type": "application/json" };

export function startConfiguration(productId: string, customerId?: string) {
  return request<ConfigurationState>("/configurations", {
    method: "POST",
    headers: json,
    body: JSON.stringify({ product_id: productId, customer_id: customerId ?? null }),
  });
}

export function getConfiguration(token: string) {
  return request<ConfigurationState>(`/configurations/${token}`);
}

export function updateConfiguration(
  token: string,
  patch: { answers?: Record<string, string | number | null>; quantity?: number; notes?: string },
) {
  return request<ConfigurationState>(`/configurations/${token}`, {
    method: "PATCH",
    headers: json,
    body: JSON.stringify(patch),
  });
}

/** Prova a riempire i campi da una frase. Se fallisce restano le domande. */
export function interpretConfiguration(token: string, text: string) {
  return request<ConfigurationState>(`/configurations/${token}/interpret`, {
    method: "POST",
    headers: json,
    body: JSON.stringify({ text }),
  });
}

export function uploadConfigurationFile(token: string, code: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return request<ConfigurationState>(
    `/configurations/${token}/files?code=${encodeURIComponent(code)}`,
    { method: "POST", body: form },
  );
}

/** Congela il prezzo e rende la configurazione ordinabile. */
export function confirmConfiguration(token: string) {
  return request<ConfigurationState>(`/configurations/${token}/confirm`, { method: "POST" });
}

/** Il totale provvisorio, che cambia a ogni risposta. */
export function formattaStima(stima: Estimate | null): string | null {
  if (!stima) return null;
  const valore = stima.show_gross ? stima.total_gross_price : stima.total_net_price;
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: stima.currency,
  }).format(Number(valore));
}

// ---------------------------------------------------------------------------
// Ordini e pagamenti
//
// Il modello: il cliente paga noi, poi noi ordiniamo dal produttore. Da qui
// esce solo la meta' che riguarda il compratore. Nessun tipo qui dentro porta
// il costo d'acquisto o il margine: l'API non li restituisce, e la vista
// `customer_orders` a database e' l'elenco chiuso di cio' che puo' uscire.
// ---------------------------------------------------------------------------
export type OrderStatus =
  | "draft" | "awaiting_payment" | "paid" | "in_production"
  | "shipped" | "delivered" | "cancelled" | "refunded";

export type VatTreatment = "standard" | "reverse_charge" | "export" | "to_confirm";

export type OrderItem = {
  id: string;
  description: string;
  specifications: Record<string, string>;
  quantity: number;
  unit_net_price: string;
  net_amount: string;
};

export type OrderState = {
  order_number: string;
  token: string;
  status: OrderStatus;
  items: OrderItem[];
  currency: string;
  net_total: string;
  vat_rate: string;
  vat_amount: string;
  gross_total: string;
  vat_treatment: VatTreatment;
  ship_to: {
    name: string | null; line1: string | null; line2: string | null;
    postal_code: string | null; city: string | null; country: string | null;
  };
  billing_name: string | null;
  billing_vat_number: string | null;
  problems: { field: string; problem: string }[];
  can_place: boolean;
  payments: {
    id: string; provider: string; provider_reference: string | null;
    amount: string; currency: string; status: string;
    captured_at: string | null; created_at: string;
  }[];
  /** Il pagamento in corso, istruzioni comprese. Null se non ce n'e' uno aperto. */
  payment: PaymentInstructions | null;
  placed_at: string | null;
  paid_at: string | null;
};

export type PaymentInstructions = {
  payment_id: string;
  provider: string;
  reference: string;
  redirect_url: string | null;
  instructions: string | null;
  amount: string;
  currency: string;
  status: string;
};

export type CustomerInput = {
  type: "business" | "consumer";
  email: string;
  full_name: string;
  company_name?: string | null;
  vat_number?: string | null;
  country_code: string;
};

export function createOrder(configurationToken: string, customer: CustomerInput) {
  return request<OrderState>("/orders", {
    method: "POST",
    headers: json,
    body: JSON.stringify({ configuration_token: configurationToken, customer }),
  });
}

export function getOrder(token: string) {
  return request<OrderState>(`/orders/${token}`);
}

export function updateOrder(
  token: string,
  patch: {
    ship_to?: {
      name: string; line1: string; line2?: string | null;
      postal_code?: string | null; city: string; country: string;
    };
    billing_name?: string;
    billing_vat_number?: string;
    billing_country?: string;
    terms_accepted?: boolean;
    custom_goods_acknowledged?: boolean;
    notes?: string;
  },
) {
  return request<OrderState>(`/orders/${token}`, {
    method: "PATCH",
    headers: json,
    body: JSON.stringify(patch),
  });
}

/** Conferma l'ordine: da qui le righe non si toccano piu'. */
export function placeOrder(token: string) {
  return request<OrderState>(`/orders/${token}/place`, { method: "POST" });
}

export function startPayment(token: string) {
  return request<PaymentInstructions>(`/orders/${token}/pay`, { method: "POST" });
}

export function formattaImporto(valore: string | number, valuta: string): string {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: valuta }).format(
    Number(valore),
  );
}

/** Come si legge un trattamento IVA per chi non fa il commercialista. */
export function spiegaIva(t: VatTreatment, aliquota: string): string {
  switch (t) {
    case "standard":
      return `IVA ${aliquota}% inclusa`;
    case "reverse_charge":
      return "Senza IVA: inversione contabile, l'imposta la assolvi tu";
    case "export":
      return "Senza IVA: cessione all'esportazione";
    default:
      return "Trattamento IVA da definire";
  }
}

export const ETICHETTE_STATO_ORDINE: Record<OrderStatus, string> = {
  draft: "Bozza",
  awaiting_payment: "In attesa di pagamento",
  paid: "Pagato",
  in_production: "In produzione",
  shipped: "Spedito",
  delivered: "Consegnato",
  cancelled: "Annullato",
  refunded: "Rimborsato",
};

/* ── Lo scaffale ──────────────────────────────────────────────────────────── */

/**
 * Una sezione del catalogo.
 *
 * `kind` non è decorativo: le sezioni di tipo `settore` sono filtri veri e
 * cliccarle equivale a scrivere la stessa cosa nella chat — quindi passano dalla
 * ricerca e producono i chip rimovibili. Le altre nascono da un fatto (un
 * ordine, una data, un flag) che una frase non può esprimere, e arrivano come
 * elenco già fatto.
 */
export type ShelfSection = {
  slug: string;
  kind: "piu_venduti" | "tendenza" | "novita" | "marchio_proprio" | "promozioni" | "settore";
  title: Localized;
  subtitle: Localized;
  sectors: string[];
  products_count: number;
  image_uri: string | null;
  /** Le prime schede del reparto, se le si è chieste con `catalogShelf(n)`. */
  products?: ProductHit[];
};

/**
 * Lo scaffale.
 *
 * `perSection` chiede anche le schede: senza, disegnare una fila di prodotti
 * per reparto costerebbe una richiesta per fila — dieci chiamate in parallelo
 * appena si apre la home.
 */
export function catalogShelf(perSection = 0) {
  const coda = perSection ? `?per_section=${perSection}` : "";
  return request<{ results: ShelfSection[] }>(`/catalog/sections${coda}`);
}

export function catalogSection(slug: string, limit = 24) {
  return request<{
    slug: string;
    kind: ShelfSection["kind"];
    title: Localized;
    subtitle: Localized;
    sectors: string[];
    total: number;
    results: ProductHit[];
  }>(`/catalog/sections/${slug}?limit=${limit}`);
}

/* ── Valutazioni ──────────────────────────────────────────────────────────── */

export function listReviews(productId: string, stelle?: number) {
  const q = new URLSearchParams();
  if (stelle) q.set("rating", String(stelle));
  const coda = q.toString();
  return request<{ product_id: string; rating: Rating; results: Review[] }>(
    `/products/${productId}/reviews${coda ? `?${coda}` : ""}`,
  );
}

/** Cosa può valutare chi ha questo token d'ordine. Vuoto prima della consegna. */
export function reviewableItems(orderToken: string) {
  return request<{ results: ReviewableItem[] }>(`/orders/${orderToken}/reviewable`);
}

export function submitReview(
  orderToken: string,
  corpo: {
    order_item_id: string;
    rating: number;
    title?: string | null;
    body?: string | null;
    author_display: string;
  },
) {
  return request<{ id: string; rating: number; product_id: string }>(
    `/orders/${orderToken}/reviews`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(corpo),
    },
  );
}

/** La media, arrotondata come si legge: «4,3». */
export function formattaMedia(voto: Rating | null | undefined): string | null {
  if (!voto || !voto.rating_avg || voto.reviews_count === 0) return null;
  return Number(voto.rating_avg).toFixed(1).replace(".", ",");
}
